import React, { useState, useRef, useCallback, useEffect, Children, Fragment, memo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import classNames from 'classnames';
import { List } from 'immutable';

import getZIndex from '../../utils/getZIndex';
import uniqueId from '../../utils/uniqueId';
import { PureDeepValue } from '../decorators/DeepValue';
import { PureChangeDetection } from '../decorators/ChangeDetection';
import FormGroup from './FormGroup';
import { Spinner } from './Spinner';
import { hasCode } from '../../middleware/activate';

const noSuggestions = List();

/**
 * General auto suggestion component.
 */
const AutoComplete = ({
  suggestions = noSuggestions,
  onSuggestionSelected,
  inputProps: inputPropsProp,
  getSuggestion,
  getSuggestionValue,
  renderSuggestion: renderSuggestionProp,
  text,
  changed = false,
  loading: loadingProp,
  formGroupClass,
  onFocus,
  focusInputOnSuggestionClick = true,
  addNew,
  getNextSuggestion,
  onUpdateSuggestions,
  suggestIfEmpty = false,
  minSuggestLength = 0,
  appendToContainer,
  children,
  ...extraProps
}) => {
  const id = useRef(uniqueId()).current;
  const containerRef = useRef(null);
  const canDisplay = useRef(true);
  const cursorPositionRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (loadingProp !== undefined) {
      setLoading(loadingProp);
    }
  }, [loadingProp, suggestions]);

  const handleSuggestionsClearRequested = useCallback(() => {
    if (suggestions && suggestions.size > 0 && onUpdateSuggestions) {
      onUpdateSuggestions({ suggestions: noSuggestions });
    }
  }, [suggestions, onUpdateSuggestions]);

  useEffect(() => {
    if (cursorPositionRef.current !== null && containerRef.current?.input) {
      const { start, end } = cursorPositionRef.current;

      containerRef.current.input.setSelectionRange(start, end);
      cursorPositionRef.current = null;
    }
  }, [text]);

  const handleInputChange = useCallback(
    event => {
      event.preventDefault();
      canDisplay.current = true;
      cursorPositionRef.current = {
        start: event.target.selectionStart,
        end: event.target.selectionEnd,
      };
      if (event.target.value !== undefined) {
        if (typeof event.target.value === 'string' && onUpdateSuggestions) {
          onUpdateSuggestions({ text: event.target.value, selected: null });
        }
      }
    },
    [onUpdateSuggestions],
  );

  const handleBlur = useCallback(
    event => {
      canDisplay.current = true;
      if (inputPropsProp && inputPropsProp.onBlur) {
        return inputPropsProp.onBlur(event);
      }
    },
    [inputPropsProp],
  );

  const handleAddNew = useCallback(() => {
    onUpdateSuggestions({ suggestions: noSuggestions });
    addNew(text);
  }, [onUpdateSuggestions, addNew, text]);

  const handleScroll = useCallback(
    event => {
      // Stop highlight suggestion item to prevent scrolling jumps when users scroll up/down
      if (getNextSuggestion) {
        const { target } = event;
        const height = target.clientHeight;
        const currentBottom = target.scrollTop + target.clientHeight;
        const maxBottom = target.scrollHeight;

        if (currentBottom + height > maxBottom) {
          getNextSuggestion({ value: text });
        }
      }
    },
    [getNextSuggestion, text],
  );

  const handleSuggestionsSelected = useCallback(
    (suggests, ...rest) => {
      cursorPositionRef.current = null;
      setTimeout(() => {
        canDisplay.current = true;
      }, 0);

      if (suggests && suggests.value === 'add new') {
        return handleAddNew();
      }
      onSuggestionSelected(suggests, ...rest);
    },
    [handleAddNew, onSuggestionSelected],
  );

  const shouldSuggest = useCallback(
    value => {
      if (suggestIfEmpty) return true;
      if (!canDisplay.current) return false;

      return addNew || (value || '').trim().length > minSuggestLength;
    },
    [suggestIfEmpty, addNew, minSuggestLength],
  );

  const getLoader = () => {
    if (!loading) return null;

    return (
      <ul className="react-autosuggest__suggestions-list" role="listbox">
        <li className="react-autosuggest__suggestion" role="option">
          <Spinner size="small" inline /> Loading...
        </li>
      </ul>
    );
  };

  const getNoData = () => {
    if (loading) return null;
    if (suggestions === noSuggestions) return null;
    if (!text || text.length === 0) return null;
    if (!suggestions) return null;
    if (suggestions.size > 0) return null;

    return (
      <ul className="react-autosuggest__suggestions-list" role="listbox">
        <li className="react-autosuggest__suggestion" role="option">
          No data
        </li>
      </ul>
    );
  };

  const getSuggestions = suggests => {
    if (!addNew) return suggests;

    return [
      {
        text: 'Add New...',
        style: { display: 'block' },
        value: 'add new',
        onClick: handleAddNew,
      },
      ...suggests,
    ];
  };

  const saveRef = ref => {
    containerRef.current = ref;
    if (!inputPropsProp) return;
    if (!inputPropsProp.ref) return;
    inputPropsProp.ref(ref);
  };

  const renderSuggestion = (...rest) => (
    <div className="react-autosuggest__suggestion-wrapper">{renderSuggestionProp(...rest)}</div>
  );

  const renderSuggestionsContainer = ({ children: containerChildren, containerProps }) => {
    if (!containerRef.current) return null;

    const targetElement = containerRef.current.input;

    if (!targetElement) return null;

    const bodyRect = document.documentElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const top = appendToContainer ? '20px' : targetRect.bottom - bodyRect.top;
    const left = appendToContainer ? 0 : targetRect.left - bodyRect.left;
    const width = Math.max(targetRect.width, 150);
    const noData = getNoData();
    const loader = getLoader();
    const zIndex = getZIndex(targetElement) + 10;

    const style = {
      position: 'absolute',
      top,
      left,
      width,
      border: '1px solid gray',
      background: '#FFF',
      maxHeight: '200px',
      overflowY: 'auto',
      zIndex,
    };

    const divClass = classNames('autocomplete-div-hide-on-empty', {
      hidden: !(containerChildren || loader || noData),
    });
    const { key, ...rest } = containerProps;
    const container = (
      <div {...rest} className={divClass} onScroll={handleScroll} style={style}>
        {containerChildren}
        {loader}
        {noData}
      </div>
    );

    const bodyElement = document.querySelector('body') ?? document.body;

    if (!bodyElement) {
      return appendToContainer ? container : <Fragment />;
    }

    const portal = createPortal(container, bodyElement);

    return appendToContainer ? container : portal;
  };

  const inputProps = {
    id,
    ...inputPropsProp,
    value: text ?? '',
    onChange: handleInputChange,
    onBlur: handleBlur,
    className: classNames('form-control', inputPropsProp.className, { changed }),
  };
  const suggestionsArray = getSuggestions(suggestions ? suggestions.toJS() : []);
  const labelClassName = classNames({ hidden: !inputProps.label });
  const FormGroupClass = classNames(formGroupClass, {
    'input-group': !formGroupClass,
  });

  const wrappedGetSuggestions = (...rest) => {
    if (hasCode()) {
      setLoading(loadingProp);
      getSuggestion(...rest);
    }
  };

  return (
    <FormGroup className={FormGroupClass}>
      <label className={labelClassName} htmlFor={inputProps.id}>
        {inputProps.label}
      </label>
      <Autosuggest
        {...extraProps}
        ref={saveRef}
        focusInputOnSuggestionClick={focusInputOnSuggestionClick}
        getSuggestionValue={getSuggestionValue}
        inputProps={inputProps}
        onSuggestionsClearRequested={handleSuggestionsClearRequested}
        onSuggestionSelected={handleSuggestionsSelected}
        onSuggestionsFetchRequested={wrappedGetSuggestions}
        renderSuggestion={(suggestion, meta) => renderSuggestion(suggestion, meta)}
        renderSuggestionsContainer={renderSuggestionsContainer}
        shouldRenderSuggestions={shouldSuggest}
        suggestions={suggestionsArray}
      />
      {children}
    </FormGroup>
  );
};

AutoComplete.propTypes = {
  addNew: PropTypes.func,
  getNextSuggestion: PropTypes.func,
  getSuggestion: PropTypes.func.isRequired,
  getSuggestionValue: PropTypes.func.isRequired,
  inputProps: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  minSuggestLength: PropTypes.number,
  onSuggestionSelected: PropTypes.func.isRequired,
  onUpdateSuggestions: PropTypes.func,
  renderSuggestion: PropTypes.func.isRequired,
  suggestIfEmpty: PropTypes.bool,
};

const Extended = PureDeepValue(PureChangeDetection(memo(AutoComplete)));

export default Extended;

/**
 * @param props {Object} InputProps for AutoCompleteComponent.
 */
export const InputProps = ({ children, ...inputProps }) => {
  let result = children;

  Children.map(children, child => {
    const { inputProps: childInputProps = {}, ...rest } = child.props;
    const inputPropObject = {
      inputProps: { ...childInputProps, ...inputProps },
    };
    const Component = child.type;

    result = <Component {...inputPropObject} {...rest} />;
  });

  return result;
};
